import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Clock, Calendar, MoreHorizontal, Trash2, Play, Filter, Settings, Flag, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutGrid, Bookmark } from 'lucide-react';
import TaskDetailDrawer from '@/components/TaskDetailDrawer';
import ColumnSettingsDialog from '@/components/ColumnSettingsDialog';
import BookmarksView from '@/components/BookmarksView';

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const getColumnColor = (color) => {
  const colors = {
    zinc: 'bg-zinc-500',
    cyan: 'bg-cyan-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    violet: 'bg-violet-500',
    blue: 'bg-blue-500',
    pink: 'bg-pink-500',
  };
  return colors[color] || colors.zinc;
};

const PRIORITY_COLORS = {
  low: 'bg-zinc-500',
  medium: 'bg-blue-500',
  high: 'bg-amber-500',
  urgent: 'bg-red-500',
};

export default function ProjectPage() {
  const { projectId } = useParams();
  const { user } = useAuthStore();
  const {
    currentProject,
    setCurrentProject,
    tasks,
    fetchTasks,
    createTask,
    deleteTask,
    moveTask,
    startTimer,
    activeTimer,
    projects,
    fetchProjectMembers,
    selectedTask,
    setSelectedTask,
    filters,
    setFilters,
    updateProject,
    isLoading,
  } = useAppStore();

  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskStatus, setTaskStatus] = useState('backlog');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [projectMembers, setProjectMembers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  const loadData = useCallback(async () => {
    if (projectId) {
      const project = projects.find((p) => p.id === projectId);
      if (project) {
        setCurrentProject(project);
      }
      await fetchTasks(projectId);
      const members = await fetchProjectMembers(projectId);
      setProjectMembers(members);
    }
  }, [projectId, projects, setCurrentProject, fetchTasks, fetchProjectMembers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (currentProject && filters) {
      fetchTasks(currentProject.id, filters);
    }
  }, [filters, currentProject, fetchTasks]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({
        title: taskTitle,
        description: taskDesc,
        project_id: projectId,
        status: taskStatus,
        priority: taskPriority,
        due_date: taskDueDate || null,
        assigned_users: selectedAssignees,
      });
      toast.success('Task created!');
      setIsTaskDialogOpen(false);
      setTaskTitle('');
      setTaskDesc('');
      setTaskStatus('backlog');
      setTaskPriority('medium');
      setTaskDueDate('');
      setSelectedAssignees([]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    const newOrder = destination.index;
    try {
      await moveTask(draggableId, newStatus, newOrder);
    } catch (err) {
      toast.error('Failed to move task');
    }
  };

  const handleStartTimer = async (taskId) => {
    if (activeTimer) {
      toast.error('Stop current timer first');
      return;
    }
    try {
      await startTimer(taskId);
      toast.success('Timer started!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      toast.success('Task deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getTasksByStatus = (statusId) => {
    return tasks.filter((t) => t.status === statusId).sort((a, b) => a.column_order - b.column_order);
  };

  const statuses = currentProject?.statuses || [];

  if (!currentProject) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <p className="text-zinc-400">Loading project...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 h-full flex flex-col" data-testid="project-page">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-white font-['Manrope']">{currentProject.name}</h1>
        <p className="text-zinc-400 text-sm mt-1">{currentProject.description || 'No description'}</p>
      </div>

      <Tabs defaultValue="tasks" className="flex-1 flex flex-col overflow-hidden mt-4">
        <div className="flex items-center justify-between mb-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="tasks" className="data-[state=active]:bg-zinc-800">
              <LayoutGrid className="w-4 h-4 mr-2" /> Tasks
            </TabsTrigger>
            <TabsTrigger value="bookmarks" className="data-[state=active]:bg-zinc-800">
              <Bookmark className="w-4 h-4 mr-2" /> Bookmarks
            </TabsTrigger>
          </TabsList>

          {/* Only show these controls in Tasks view, actually easier to just condition render or move them inside TabsContent if they are specific. 
              The Filters and Add Task are specific to Tasks. The Header (Title) is common.
          */}
        </div>

        <TabsContent value="tasks" className="flex-1 flex flex-col overflow-hidden data-[state=inactive]:hidden mt-0">
          <div className="flex justify-end gap-2 mb-4">
            {/* Filters */}
            <DropdownMenu open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:text-white" data-testid="filter-btn">
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {(filters.assignee !== 'all' || filters.priority !== 'all') && (
                    <span className="ml-2 w-2 h-2 rounded-full bg-cyan-500" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-zinc-900 border-zinc-800 p-4" align="end">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1.5">Assignee</label>
                    <Select value={filters.assignee} onValueChange={(v) => setFilters({ assignee: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="filter-assignee">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="self">My Tasks</SelectItem>
                        {projectMembers.map((m) => (
                          <SelectItem key={m.user_id} value={m.user_id}>
                            {m.user?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1.5">Priority</label>
                    <Select value={filters.priority} onValueChange={(v) => setFilters({ priority: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="filter-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full text-zinc-400"
                    onClick={() => setFilters({ assignee: 'all', priority: 'all' })}
                  >
                    Clear Filters
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Column Settings */}
            <Button
              variant="outline"
              className="border-zinc-800 text-zinc-400 hover:text-white"
              onClick={() => setIsColumnSettingsOpen(true)}
              data-testid="column-settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>

            {/* Add Task */}
            <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="create-task-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Task</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Title</label>
                    <Input
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      placeholder="Task title"
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="task-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Description</label>
                    <Textarea
                      value={taskDesc}
                      onChange={(e) => setTaskDesc(e.target.value)}
                      placeholder="Task description..."
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="task-desc-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Status</label>
                      <Select value={taskStatus} onValueChange={setTaskStatus}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="task-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {statuses.map((col) => (
                            <SelectItem key={col.id} value={col.id}>
                              {col.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Priority</label>
                      <Select value={taskPriority} onValueChange={setTaskPriority}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="task-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Due Date</label>
                    <Input
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="task-due-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Assignees</label>
                    <div className="flex flex-wrap gap-2">
                      {projectMembers.map((member) => (
                        <button
                          key={member.user_id}
                          type="button"
                          onClick={() => {
                            setSelectedAssignees((prev) =>
                              prev.includes(member.user_id)
                                ? prev.filter((id) => id !== member.user_id)
                                : [...prev, member.user_id]
                            );
                          }}
                          className={`flex items-center gap-1 px-2 py-1 rounded border text-sm ${selectedAssignees.includes(member.user_id)
                            ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400'
                            : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                            }`}
                        >
                          <User className="w-3 h-3" />
                          {member.user?.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 text-black hover:bg-cyan-400" data-testid="create-task-submit-btn">
                    Create Task
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Kanban Board */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-4 md:gap-6 overflow-x-auto flex-1 pb-4" data-testid="kanban-board">
              {statuses.map((column) => (
                <div key={column.id} className="kanban-column flex-shrink-0" data-testid={`kanban-column-${column.id}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-2 h-2 rounded-full ${getColumnColor(column.color)}`} />
                    <h3 className="text-sm font-medium text-white">{column.title}</h3>
                    {column.is_in_progress && (
                      <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[10px] py-0 px-1.5 h-4 hover:bg-cyan-500/10">
                        <Clock className="w-2.5 h-2.5 mr-1" />
                        Working
                      </Badge>
                    )}
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded ml-auto">
                      {getTasksByStatus(column.id).length}
                    </span>
                  </div>

                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 min-h-[200px] space-y-3 ${snapshot.isDraggingOver ? 'bg-zinc-800/30 rounded-lg' : ''}`}
                      >
                        {isLoading && getTasksByStatus(column.id).length === 0 ? (
                          [1, 2, 3].map((i) => (
                            <div key={i} className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 animate-pulse">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-zinc-800" />
                                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                              </div>
                              <div className="h-2 bg-zinc-800 rounded w-full mb-2" />
                              <div className="h-2 bg-zinc-800 rounded w-2/3" />
                            </div>
                          ))
                        ) : (
                          getTasksByStatus(column.id).map((task, index) => (
                            <Draggable key={task.id} draggableId={task.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => setSelectedTask(task)}
                                  className={`task-card ${snapshot.isDragging ? 'kanban-dragging shadow-xl' : ''}`}
                                  data-testid={`task-card-${task.id}`}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-1.5 h-1.5 rounded-full ${PRIORITY_COLORS[task.priority]}`} />
                                      <h4 className="text-sm font-medium text-white line-clamp-2">{task.title}</h4>
                                    </div>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <button className="text-zinc-500 hover:text-white p-1" data-testid={`task-menu-${task.id}`}>
                                          <MoreHorizontal className="w-4 h-4" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                                        <DropdownMenuItem
                                          onClick={(e) => { e.stopPropagation(); handleStartTimer(task.id); }}
                                          className="text-cyan-400 focus:text-cyan-300 focus:bg-zinc-800"
                                          data-testid={`start-timer-${task.id}`}
                                        >
                                          <Play className="w-4 h-4 mr-2" /> Start Timer
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator className="bg-zinc-800" />
                                        <DropdownMenuItem
                                          onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                          className="text-red-400 focus:text-red-300 focus:bg-zinc-800"
                                          data-testid={`delete-task-${task.id}`}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>

                                  {task.description && (
                                    <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{task.description}</p>
                                  )}

                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {task.due_date && (
                                        <span className="flex items-center gap-1 text-xs text-zinc-500">
                                          <Calendar className="w-3 h-3" />
                                          {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      )}
                                      {task.total_time_seconds > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-cyan-400 font-['JetBrains_Mono']">
                                          <Clock className="w-3 h-3" />
                                          {formatTime(task.total_time_seconds)}
                                        </span>
                                      )}
                                    </div>

                                    {task.assigned_user_details?.length > 0 && (
                                      <div className="flex -space-x-2">
                                        {task.assigned_user_details.slice(0, 3).map((user) => (
                                          <Avatar key={user.id} className="w-6 h-6 border-2 border-zinc-900">
                                            <AvatarFallback className="bg-zinc-700 text-xs">
                                              {user.name?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                          </Avatar>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        </TabsContent>

        <TabsContent value="bookmarks" className="mt-0">
          <BookmarksView projectId={currentProject.id} />
        </TabsContent>
      </Tabs>

      {/* Task Detail Drawer */}
      {selectedTask && (
        <TaskDetailDrawer
          task={selectedTask}
          projectMembers={projectMembers}
          statuses={statuses}
          onClose={() => setSelectedTask(null)}
        />
      )}

      {/* Column Settings Dialog */}
      {isColumnSettingsOpen && (
        <ColumnSettingsDialog
          project={currentProject}
          onClose={() => setIsColumnSettingsOpen(false)}
          onSave={async (newStatuses) => {
            await updateProject(currentProject.id, { statuses: newStatuses });
            toast.success('Columns updated');
            setIsColumnSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}
